// Components==============
import indexGraphData from 'actions/graphData/index';
import storeWrongAnswer from 'actions/wrongAnswer/store';
import ControlBar from 'components/Layout/ControlBar/ControlBar';
import ExerciseBottom from 'components/UI/ExerciseBottom/ExerciseBottom';
import ExerciseWrapper from 'components/UI/ExerciseWrapper/ExerciseWrapper';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FlashContext } from 'screens/Flash/Flash';
import shuffleArray from 'utilities/shuffleArray';
import FlashBox from './components/FlashBox/FlashBox';
import './FlashExercise.module.scss';
// =========================

type FlashExerciseContextTypes = {
    answer?: Words;
    displayCard: boolean;
};

export const FlashExerciseContext = createContext(
    {} as FlashExerciseContextTypes
);

export default function FlashExercise() {
    const {
        exercise,
        setIsFinished,
        setBarStatus,
        barStatus,
        isFinished,
        current,
        setCurrent,
    } = useContext(FlashContext);

    const [answer, setAnswer] = useState(exercise?.words?.[current]);
    const [words, setWords] = useState<Words[]>([]);
    const [displayCard, setDisplayCard] = useState(false);
    const [toggleDisplayCard, setToggleDisplayCard] = useState(false);

    const [displayReplayIcon, setDisplayReplayIcon] = useState(false);

    const dispatch = useDispatch();

    const { treatmentId, exerciseId } = useParams() as {
        treatmentId: string;
        exerciseId: string;
    };

    const shouldPopulateNewWords = words?.length && current === words.length;

    useEffect(() => {
        if (!exercise?.words?.length) return;

        const localWords = exercise.words;

        // If user has run out of words, recreate and randomize the same group and add it to the array
        shuffleArray(localWords);
        setWords((prev) => (prev ? [...prev, ...localWords] : localWords));
    }, [exercise?.id, shouldPopulateNewWords === true]);

    useEffect(() => {
        setAnswer(words?.[current]);
    }, [current, words?.length]);

    const storeWrong = () => {
        dispatch(
            storeWrongAnswer({
                given_answer: `${current + 1}`,
                correct_answer: `${exercise?.pivot.needs_correct}`,
                type: 'flash',
                exercise_id: exerciseId,
                exercise_type: 'flash',
                treatment_id: treatmentId,
            })
            // @ts-ignore
        ).then(() => dispatch(indexGraphData()));
    };

    useEffect(() => {
        setToggleDisplayCard((prev) => !prev);
    }, [answer]);

    useEffect(() => {
        if (barStatus === 'loaded') {
            // Exercise time is finished
            setIsFinished(true);
            storeWrong();
        }
    }, [barStatus, current]);

    useEffect(() => {
        setDisplayCard(true);

        const timer = setTimeout(() => {
            setDisplayCard(false);
        }, exercise?.pivot?.word_duration);

        return () => clearTimeout(timer);
    }, [toggleDisplayCard]);

    function handleClick() {
        if (current === -1) {
            setBarStatus('loading');
            setCurrent((prev) => prev + 1);
            setDisplayReplayIcon(true);
            return;
        }

        setToggleDisplayCard((prev) => !prev);
    }

    function handleEyeIconClick() {
        setDisplayCard((prev) => !prev);
    }

    function handleNextClick() {
        if (displayCard) setDisplayCard(false);
        setCurrent((prev) => prev + 1);
    }

    return (
        <FlashExerciseContext.Provider value={{ answer, displayCard }}>
            <div styleName="flash-exercise">
                <ExerciseWrapper>
                    {!isFinished && (
                        <div styleName="wrapper">
                            {words &&
                                words.map((word, i: number) => (
                                    <FlashBox key={i} word={word} />
                                ))}
                        </div>
                    )}
                </ExerciseWrapper>
                <ExerciseBottom
                    controlBar={
                        <>
                            {!isFinished && (
                                <ControlBar
                                    exerciseStatus="started"
                                    next={
                                        current === -1
                                            ? null
                                            : () => handleNextClick()
                                    }
                                    flashExerciseStart={current === -1}
                                    handlePlayClick={handleClick}
                                    displayReplayIcon={displayReplayIcon}
                                    preventAutoPlay
                                    handleEyeIconClick={handleEyeIconClick}
                                />
                            )}
                        </>
                    }
                />
            </div>
        </FlashExerciseContext.Provider>
    );
}
